import React, { useState, useEffect } from 'react'
import { Briefcase, MapPin, Clock, DollarSign, ArrowRight, Filter, Send, Upload, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

const Careers = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all')
  const [selectedExperience, setSelectedExperience] = useState('all')
  const [selectedLocation, setSelectedLocation] = useState('all')
  const [jobs, setJobs] = useState([])
  const [selectedJob, setSelectedJob] = useState(null)
  const [showApplyForm, setShowApplyForm] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const { register, handleSubmit, formState: { errors }, reset } = useForm()

  // ...existing code for departments, experienceLevels, locations, sampleJobs...

  const departments = [
    { id: 'all', name: 'All Departments' },
    { id: 'production', name: 'Production' },
    { id: 'technical', name: 'Technical' },
    { id: 'creative', name: 'Creative' },
    { id: 'admin', name: 'Administration' }
  ]

  const experienceLevels = [
    { id: 'all', name: 'All Levels' },
    { id: 'entry', name: 'Entry Level (0-2 years)' },
    { id: 'mid', name: 'Mid Level (3-5 years)' },
    { id: 'senior', name: 'Senior Level (5+ years)' }
  ]

  const locations = [
    { id: 'all', name: 'All Locations' },
    { id: 'dhaka', name: 'Dhaka' },
    { id: 'remote', name: 'Remote' },
    { id: 'hybrid', name: 'Hybrid' }
  ]

  const sampleJobs = [
    {
      id: 1,
      title: 'Senior Film Producer',
      department: 'production',
      location: 'dhaka',
      locationType: 'Dhaka, Bangladesh',
      type: 'Full-time',
      experience: 'senior',
      salary: 'Competitive',
      description: 'Lead production of feature films and drama series from pre-production through post-production.',
      responsibilities: [
        'Oversee all aspects of film production',
        'Manage production budgets and schedules',
        'Coordinate with directors, actors, and crew',
        'Ensure quality standards are met'
      ],
      requirements: [
        '5+ years film production experience',
        'Strong leadership and communication skills',
        'Knowledge of Bangladeshi film industry',
        'Portfolio of completed projects'
      ]
    },
    {
      id: 2,
      title: 'Video Editor',
      department: 'technical',
      location: 'dhaka',
      locationType: 'Dhaka, Bangladesh',
      type: 'Full-time',
      experience: 'mid',
      salary: 'BDT 40,000 - 60,000/month',
      description: 'Edit and assemble recorded footage into finished projects that match director\'s vision.',
      responsibilities: [
        'Edit video footage using professional software',
        'Color correction and grading',
        'Sound editing and mixing',
        'Collaborate with directors on final cuts'
      ],
      requirements: [
        '3+ years video editing experience',
        'Proficiency in Adobe Premiere, Final Cut Pro',
        'Strong attention to detail',
        'Creative storytelling ability'
      ]
    }
  ]

  useEffect(() => {
    const savedJobs = localStorage.getItem('shikdar_films_jobs')
    if (savedJobs) {
      setJobs(JSON.parse(savedJobs))
    } else {
      setJobs(sampleJobs)
      localStorage.setItem('shikdar_films_jobs', JSON.stringify(sampleJobs))
    }
  }, [])

  const filteredJobs = jobs.filter(job => {
    const departmentMatch = selectedDepartment === 'all' || job.department === selectedDepartment
    const experienceMatch = selectedExperience === 'all' || job.experience === selectedExperience
    const locationMatch = selectedLocation === 'all' || job.location === selectedLocation
    return departmentMatch && experienceMatch && locationMatch
  })

  const handleApplyClick = (job) => {
    setSelectedJob(job)
    setShowApplyForm(true)
    setUploadedFile(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload PDF or Word document only')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB')
        return
      }

      // Read file as base64 for storage
      const reader = new FileReader()
      reader.onload = (event) => {
        setUploadedFile({
          name: file.name,
          size: file.size,
          type: file.type,
          data: event.target.result
        })
        toast.success('Resume uploaded successfully!')
      }
      reader.readAsDataURL(file)
    }
  }

  const removeFile = () => {
    setUploadedFile(null)
    toast.success('File removed')
  }

  const onSubmitApplication = async (data) => {
    if (!uploadedFile) {
      toast.error('Please upload your resume/portfolio')
      return
    }

    const application = {
      ...data,
      jobId: selectedJob.id,
      jobTitle: selectedJob.title,
      resume: uploadedFile,
      timestamp: new Date().toISOString(),
      id: Date.now(),
      status: 'pending'
    }

    const applications = JSON.parse(localStorage.getItem('shikdar_films_job_applications') || '[]')
    applications.push(application)
    localStorage.setItem('shikdar_films_job_applications', JSON.stringify(applications))

    // Send notification
    const notifications = JSON.parse(localStorage.getItem('shikdar_films_notifications') || '[]')
    notifications.push({
      id: Date.now(),
      type: 'job_application',
      title: 'New Job Application',
      message: `${data.name} applied for ${selectedJob.title}`,
      timestamp: new Date().toISOString(),
      read: false
    })
    localStorage.setItem('shikdar_films_notifications', JSON.stringify(notifications))

    toast.success('Application submitted successfully! We will contact you soon.')
    reset()
    setUploadedFile(null)
    setShowApplyForm(false)
    setSelectedJob(null)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Briefcase className="w-12 h-12 text-bengal-green" />
            <h1 className="section-title text-4xl md:text-5xl text-gray-900">
              Careers at Shikdar Films
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join our team and be part of creating award-winning films and dramas that showcase Bangladeshi culture to the world.
          </p>
        </div>

        {/* Application Form Modal */}
        {showApplyForm && selectedJob && (
          <div className="bg-white rounded-xl shadow-md p-8 mb-12 border-l-4 border-bengal-green">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title text-2xl text-gray-900">
                Apply for {selectedJob.title}
              </h2>
              <button
                onClick={() => {
                  setShowApplyForm(false)
                  setSelectedJob(null)
                  setUploadedFile(null)
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmitApplication)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    {...register('phone', { required: 'Phone number is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                  />
                  {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience *</label>
                  <input
                    type="number"
                    {...register('experience', { required: 'Experience is required', min: 0 })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                  />
                  {errors.experience && <p className="mt-1 text-sm text-red-600">{errors.experience.message}</p>}
                </div>
              </div>

              {/* File Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resume/Portfolio * (PDF or Word, max 5MB)</label>
                {!uploadedFile ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-bengal-green transition-colors">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <label className="cursor-pointer">
                      <span className="text-bengal-green hover:text-bengal-orange font-medium">Click to upload</span>
                      <span className="text-gray-600"> or drag and drop</span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">PDF or Word document (max 5MB)</p>
                  </div>
                ) : (
                  <div className="border-2 border-bengal-green rounded-lg p-4 bg-green-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-bengal-green rounded-lg flex items-center justify-center">
                          <Upload className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                          <p className="text-sm text-gray-600">{(uploadedFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cover Letter *</label>
                <textarea
                  rows="5"
                  {...register('coverLetter', { required: 'Cover letter is required' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent resize-none"
                  placeholder="Tell us why you're the perfect fit for this role..."
                ></textarea>
                {errors.coverLetter && <p className="mt-1 text-sm text-red-600">{errors.coverLetter.message}</p>}
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-bengal-green hover:bg-bengal-orange text-white font-semibold px-8 h-14 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                >
                  <Send className="w-5 h-5" />
                  Submit Application
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowApplyForm(false)
                    setSelectedJob(null)
                    setUploadedFile(null)
                  }}
                  className="px-8 h-14 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ...existing filters and jobs list code... */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-bengal-green" />
            <h2 className="text-lg font-semibold text-gray-900">Filter Jobs</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
              >
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
              <select
                value={selectedExperience}
                onChange={(e) => setSelectedExperience(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
              >
                {experienceLevels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
              >
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredJobs.length} {filteredJobs.length === 1 ? 'position' : 'positions'}
          </div>
        </div>

        <div className="space-y-6">
          {filteredJobs.map((job) => (
            <div 
              key={job.id}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-8 border-l-4 border-bengal-green"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3 section-title">
                    {job.title}
                  </h2>
                  
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-bengal-green" />
                      <span className="text-sm">{job.locationType}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4 text-bengal-green" />
                      <span className="text-sm">{job.type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4 text-bengal-green" />
                      <span className="text-sm">{job.salary}</span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {job.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Responsibilities:</h3>
                      <ul className="space-y-1">
                        {job.responsibilities.map((resp, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="w-1.5 h-1.5 bg-bengal-green rounded-full mt-1.5 flex-shrink-0"></span>
                            {resp}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Requirements:</h3>
                      <ul className="space-y-1">
                        {job.requirements.map((req, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="w-1.5 h-1.5 bg-bengal-orange rounded-full mt-1.5 flex-shrink-0"></span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleApplyClick(job)}
                  className="inline-flex items-center justify-center gap-2 bg-bengal-green hover:bg-bengal-orange text-white font-semibold px-6 h-12 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
                >
                  Apply Now
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredJobs.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow-md">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No positions match your filters</p>
            <p className="text-gray-400 text-sm">Try adjusting your search criteria</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Careers